import { useState } from 'react'
import {
  Alert,
  Typography,
  Paper,
  IconButton,
  Collapse,
  Button,
  Snackbar,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useLocation, useNavigate } from 'react-router-dom'
import usePermissions from '@/utils/hooks/users'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import { useRequestUpgrade } from '@/utils/hooks/subscriptions'
import { isFreeTier, getEffectiveOrgUuid } from '@/utils/utils'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { UUID } from 'crypto'

const DISMISS_KEY = 'subscriptionPitchDismissed'

const getSessionDismissed = (): boolean => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === 'true'
  } catch {
    return false
  }
}

const setSessionDismissed = () => {
  try {
    sessionStorage.setItem(DISMISS_KEY, 'true')
  } catch {
    // sessionStorage unavailable (e.g. private browsing restrictions)
  }
}

export default function SubscriptionPitchBanner() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const location = useLocation()
  const { permissions, isLoading: permissionsLoading } = usePermissions()
  const { user, token, orgs, hasAgreedToTerms } = getCurrentUser()
  const { orgData, isLoading: orgsLoading } = useCurrentUserOrgs(
    token as string,
    user,
    orgs as UUID[],
  )

  const [dismissed, setDismissed] = useState(getSessionDismissed)
  const upgradeMutation = useRequestUpgrade()

  const currentOrg =
    orgData?.find((org) => org.uuid === getEffectiveOrgUuid(user, orgs)) || null
  const isAdmin = permissions?.role === 'admin'
  // Hide on the accounts page where subscription options are already visible
  const isOnAccountsPage = location.pathname === '/profile/accounts'

  const shouldShow =
    token &&
    hasAgreedToTerms &&
    currentOrg &&
    !orgsLoading &&
    !permissionsLoading &&
    !isOnAccountsPage &&
    isFreeTier(currentOrg)

  if (!shouldShow) {
    return null
  }

  const handleDismiss = () => {
    setDismissed(true)
    setSessionDismissed()
  }

  const handleSubscribe = () => {
    navigate('/profile/accounts')
  }

  const handleRequestUpgrade = () => {
    if (!currentOrg?.uuid || !token) return
    upgradeMutation.mutate({ orgUuid: currentOrg.uuid as UUID, token })
  }

  return (
    <>
      <Collapse in={!dismissed}>
        <Paper
          elevation={3}
          sx={{
            position: 'fixed',
            bottom: isMobile ? '100px' : 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            borderRadius: 0,
            borderTop: '1px solid #e0e0e0',
            width: isMobile ? '70%' : '80%',
          }}
        >
          <Alert
            severity="warning"
            role="note"
            aria-label="Subscription upgrade prompt"
            sx={{
              alignItems: 'center',
              borderRadius: 0,
              '& .MuiAlert-message': {
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
              },
            }}
            action={
              <IconButton
                onClick={handleDismiss}
                size="small"
                sx={{ color: 'inherit' }}
                aria-label="dismiss upgrade prompt"
              >
                <CloseIcon />
              </IconButton>
            }
          >
            <Typography variant="body2" sx={{ mr: 2 }}>
              You're on the Free Plan. Upgrade to a Paid plan to unlock
              unlimited matches, applications, and more.
            </Typography>
            {isAdmin ? (
              <Button
                variant="contained"
                size="small"
                disableRipple
                onClick={handleSubscribe}
                sx={{
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  width: '150px',
                }}
              >
                Upgrade Account
              </Button>
            ) : (
              <Button
                variant="contained"
                size="small"
                disableRipple
                onClick={handleRequestUpgrade}
                disabled={upgradeMutation.isPending || upgradeMutation.isSuccess}
                sx={{
                  whiteSpace: 'wrap',
                  textTransform: 'none',
                  width: '100%',
                  maxWidth: '300px',
                }}
              >
                {upgradeMutation.isSuccess
                  ? 'Request Sent'
                  : 'Send account admin a request to upgrade'}
              </Button>
            )}
          </Alert>
        </Paper>
      </Collapse>
      <Snackbar
        open={upgradeMutation.isError}
        autoHideDuration={3000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return
          upgradeMutation.reset()
        }}
        message="Failed to send upgrade request"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        aria-label="Upgrade request error notification"
      />
    </>
  )
}
